import { useState } from "react"
import {
  TouchableOpacity,
  View,
  Image,
  Linking,
  ScrollView,
} from "react-native"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { H2, H3, H5, H6, Body, Tiny } from "@/components/standard/BFText"
import { BFScreen } from "@/components/standard/screen/BFScreen"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { useInfiniteQuery } from "@tanstack/react-query"
import { router } from "expo-router"
import { useViewedCallback } from "@/analytics/react"
import { loanListViewed, loanDashboardEntryClicked, loanViewRatesClicked, loanGetQuoteClicked, loanCardClicked } from "@/analytics/events/loans"
import { loanDashboardListInfiniteQueryOptions } from "@/queries/loan-dashboard"
import FullScreenLoader from "@/components/loading/FullScreenLoaderDark"
import { ProductPopup } from "@/screens/loans/components/ProductPopup"
import { Button } from "@/components/reusables/ui/button"
import ArrowUpRight from "phosphor-react-native/src/icons/ArrowUpRight"
import { Card, CardContent } from "@/components/reusables/ui/card"
import { BFColors } from "@/constants/Colors"
import {
  loanAppLoanWizard,
  externalDashboardUrl,
  externalLoanDashboardUrl,
  isUnsubmittedDraft,
} from "@/screens/loans/utils/LoanUtils"
import {
  openDraftQuote,
  openNewLoanQuote,
} from "@/screens/loans/utils/openNewLoanQuote"
import type { LoanStatus } from "@/generated/graphql"

interface LoanCardProps {
  onPress?: () => void
}

function FixedBottomQuoteButton() {
  const handlePress = () => {
    loanGetQuoteClicked({
      currentScreen: "Loan List",
    })
    openNewLoanQuote()
  }

  return (
    <View
      className="absolute bottom-0 left-0 right-0 pb-[20px]"
    >
      <View className="px-2 pt-4">
        <TouchableOpacity
          onPress={handlePress}
          className="bg-calm-blue rounded-full py-4 items-center shadow-2xl"
          testID="get-quote-button"
        >
          <H5 className="text-white font-bold">Get a quote</H5>
        </TouchableOpacity>
      </View>
    </View>
  )
}

function LoanCard({ onPress }: LoanCardProps) {
  return (
    <TouchableOpacity onPress={onPress} testID="open-loan-dashboard">
      <Card className="mb-4 shadow-lg">
        <CardContent className="p-6">
          <Horizontal className="items-center justify-between">
            <Vertical className="flex-1">
              <H3 color="primary" className="mb-2">
                Loan Dashboard
              </H3>
              <Body color="hint">View quotes and manage loans</Body>
            </Vertical>
            <View className="ml-4 bg-gray-100 rounded-full px-5 py-2.5">
              <Horizontal className="items-center gap-2">
                <H6 className="text-calm-blue">Open</H6>
                <ArrowUpRight size={16} color={BFColors.calmBlue} weight="bold" />
              </Horizontal>
            </View>
          </Horizontal>
        </CardContent>
      </Card>
    </TouchableOpacity>
  )
}

interface LoanCardData {
  id: string
  status: LoanStatus
  externalLoanId?: string | null
  property: {
    heroImage?: string | null
    street?: string | null
    city?: string | null
    state?: string | null
    zipcode?: string | null
  }
}

interface DraftLoanCardProps {
  loan: LoanCardData
}

function DraftLoanCard({ loan }: DraftLoanCardProps) {
  const address = loan.property.street || "No address"
  const location =
    `${loan.property.city || ""}, ${loan.property.state || ""} ${loan.property.zipcode || ""}`.trim()

  const onPress = () => {
    loanCardClicked({
      currentScreen: "Loan List",
      loanId: loan.id,
      externalLoanId: loan.externalLoanId,
      loanStatus: "draft",
      propertyAddress: loan.property.street,
    })
    // Drafts are completed in the dashboard; the native wizard only catches
    // the rare draft whose deal id hasn't landed yet.
    if (!openDraftQuote(loan.externalLoanId)) {
      router.push(loanAppLoanWizard(loan.id))
    }
  }

  return (
    <TouchableOpacity onPress={onPress} testID="draft-loan-card" className="mr-4">
      <Card className="shadow w-40 overflow-hidden">
        <CardContent className="p-0">
          <Vertical>
            {loan.property.heroImage && (
              <View className="w-full h-32">
                <Image
                  source={{ uri: loan.property.heroImage }}
                  className="w-full h-full"
                  resizeMode="cover"
                />
                <View className="absolute top-3 left-3 bg-warning rounded px-2 py-1">
                  <H6 className="text-warning-foreground text-xs font-bold">DRAFT</H6>
                </View>
              </View>
            )}
            <Vertical className="p-3">
              <H6 color="primary" className="text-sm mb-1" numberOfLines={1}>
                {address}
              </H6>
              <Body color="hint" className="text-xs" numberOfLines={1}>
                {location}
              </Body>
            </Vertical>
          </Vertical>
        </CardContent>
      </Card>
    </TouchableOpacity>
  )
}

interface DraftLoansSectionProps {
  loans: LoanCardData[]
}

function DraftLoansSection({ loans }: DraftLoansSectionProps) {
  if (loans.length === 0) {
    return null
  }

  return (
    <Vertical className="gap-0 mt-6">
      <H5 color="primary" className="mb-1">
        Continue your quote request
      </H5>
      <View className="-mx-8">
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingLeft: 32, paddingRight: 32, paddingVertical: 8 }}
        >
          {loans.map((loan) => (
            <DraftLoanCard key={loan.id} loan={loan} />
          ))}
        </ScrollView>
      </View>
    </Vertical>
  )
}

interface QuoteCardProps {
  loan: LoanCardData
}

function QuoteCard({ loan }: QuoteCardProps) {
  const address = loan.property.street || "No address"
  const location =
    `${loan.property.city || ""}, ${loan.property.state || ""} ${loan.property.zipcode || ""}`.trim()

  const onPress = () => {
    loanCardClicked({
      currentScreen: "Loan List",
      loanId: loan.id,
      externalLoanId: loan.externalLoanId,
      loanStatus: "quote",
      propertyAddress: loan.property.street,
    })
    loanDashboardEntryClicked({
      currentScreen: "Loan List",
      loanId: loan.id,
      externalLoanId: loan.externalLoanId,
    })
    const url = loan.externalLoanId
      ? externalLoanDashboardUrl(loan.externalLoanId)
      : externalDashboardUrl()
    void Linking.openURL(url)
  }

  return (
    <TouchableOpacity onPress={onPress} testID="quote-card">
      <Card className="mb-4 shadow overflow-hidden">
        <CardContent className="p-0">
          <Horizontal className="items-stretch">
            {loan.property.heroImage && (
              <View className="w-32 h-32">
                <Image
                  source={{ uri: loan.property.heroImage }}
                  className="w-full h-full"
                  resizeMode="cover"
                />
                <View className="absolute top-3 left-3 bg-calm-blue rounded px-2 py-1">
                  <H6 className="text-white text-xs">QUOTE</H6>
                </View>
              </View>
            )}
            <Vertical className="flex-1 p-4 justify-between">
              <Vertical>
                <H6 color="primary" className="mb-1" numberOfLines={1}>
                  {address}
                </H6>
                <Tiny color="hint" numberOfLines={1}>
                  {location}
                </Tiny>
              </Vertical>
              <TouchableOpacity
                onPress={onPress}
                testID="manage-quote-button"
                className="self-start"
              >
                <Horizontal className="items-center gap-1">
                  <H6 className="text-calm-blue">Manage</H6>
                  <ArrowUpRight size={16} color={BFColors.calmBlue} weight="bold" />
                </Horizontal>
              </TouchableOpacity>
            </Vertical>
          </Horizontal>
        </CardContent>
      </Card>
    </TouchableOpacity>
  )
}

interface LoansSectionProps {
  loans: LoanCardData[]
}

function LoansSection({ loans }: LoansSectionProps) {
  if (loans.length === 0) {
    return null
  }

  return (
    <Vertical className="gap-0 mt-6">
      <H5 color="primary" className="mb-2">
        Loans
      </H5>
      {loans.map((loan) => (
        <QuoteCard key={loan.id} loan={loan} />
      ))}
    </Vertical>
  )
}

function LoanListScreen() {
  const [showProductPopup, setShowProductPopup] = useState(false)
  const insets = useSafeAreaInsets()

  const loanListQuoteQuery = useInfiniteQuery({
    ...loanDashboardListInfiniteQueryOptions("quote"),
  })

  const loanListActiveQuery = useInfiniteQuery({
    ...loanDashboardListInfiniteQueryOptions("active"),
  })

  useViewedCallback(() => {
    loanListViewed()
  }, [])

  if (loanListQuoteQuery.isLoading || loanListActiveQuery.isLoading) {
    return <FullScreenLoader>Loading...</FullScreenLoader>
  }

  const quoteBucket = (loanListQuoteQuery.data || []).filter(
    (loan): loan is NonNullable<typeof loan> => loan !== null
  )

  const draftLoans = quoteBucket.filter(isUnsubmittedDraft)
  // Submitted quotes share the bucket; they belong with the loans, not the
  // "continue your request" carousel.
  const submittedQuotes = quoteBucket.filter((loan) => !isUnsubmittedDraft(loan))

  const activeLoans = [
    ...submittedQuotes,
    ...(loanListActiveQuery.data || []).filter(
      (loan): loan is NonNullable<typeof loan> => loan !== null
    ),
  ]

  return (
    <BFScreen noBackButton={true}>
      <Vertical className="flex-1 pt-8 pb-0 bg-gray-50">
        <Horizontal className="justify-between items-center px-8">
          <H2
            style={{ paddingTop: insets.top + 20, paddingBottom: 20 }}
            color="primary"
          >
            Loans
          </H2>
          <View style={{ paddingTop: insets.top + 20, paddingBottom: 20 }}>
            <Button
              variant="ghost"
              size="sm"
              className="font-figtree-bold color-accent-foreground font-bold"
              onPress={() => {
                loanViewRatesClicked({
                  currentScreen: "Loan List",
                })
                setShowProductPopup(true)
              }}
            >
              <H5 className="color-accent-foreground">View rates</H5>
            </Button>
          </View>
        </Horizontal>

        <ScrollView contentContainerStyle={{ paddingHorizontal: 32, paddingBottom: 120 }}>
          <Vertical className="gap-0">
            <LoanCard
              onPress={() => {
                loanDashboardEntryClicked({
                  currentScreen: "Loan List",
                  loanId: null,
                })
                void Linking.openURL(externalDashboardUrl())
              }}
            />
            <DraftLoansSection loans={draftLoans} />
            <LoansSection loans={activeLoans} />
          </Vertical>
        </ScrollView>
      </Vertical>
      <FixedBottomQuoteButton />
      <ProductPopup
        visible={showProductPopup}
        onClose={() => {
          setShowProductPopup(false)
        }}
        includeActions={false}
        title="Loan Products"
        description=""
      />
    </BFScreen>
  )
}

export default LoanListScreen
